import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { Plus, ArrowDownLeft, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Spinner from './Spinner.js';

const InwardsQue = ({ db, user }) => {
  const navigate = useNavigate();
  const [dispatches, setDispatches] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- RESPONSIVE STATE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth <= 768;

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState('deliveredAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    mocType: '',
    customerName: '',
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  useEffect(() => {
    setLoading(true);
    const dispatchesQuery = query(
      collection(db, "dispatches"),
      where("dispatchStatus", "==", "delivered"),
      orderBy("updatedAt", "desc")
    );

    const unsubscribe = onSnapshot(dispatchesQuery, (querySnapshot) => {
      const dispatchList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDispatches(dispatchList);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching inwards queue: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [db]);

  const getSortValue = (dispatch, column) => {
    if (column === 'deliveredAt') {
      const value = dispatch.deliveredAt?.toDate ? dispatch.deliveredAt.toDate() : dispatch.deliveredAt || dispatch.updatedAt?.toDate?.() || dispatch.updatedAt;
      return value ? new Date(value).getTime() : 0;
    }
    return dispatch[column] || '';
  };

  const filteredAndSortedDispatches = useMemo(() => {
    let filterableDispatches = [...dispatches];

    filterableDispatches = filterableDispatches.filter(dispatch => {
      const deliveredDateSource = dispatch.deliveredAt?.toDate ? dispatch.deliveredAt.toDate() : dispatch.deliveredAt || dispatch.updatedAt?.toDate?.() || dispatch.updatedAt;
      const deliveredDate = deliveredDateSource ? new Date(deliveredDateSource) : null;

      if (deliveredDate && !Number.isNaN(deliveredDate.getTime()) && (filters.startDate || filters.endDate)) {
        deliveredDate.setHours(0, 0, 0, 0);
        if (filters.startDate && deliveredDate < new Date(filters.startDate)) return false;
        if (filters.endDate && deliveredDate > new Date(filters.endDate)) return false;
      }

      if (filters.mocType && dispatch.typeOfMOC !== filters.mocType) return false;

      if (filters.customerName) {
        const haystack = [
          dispatch.customerName,
          dispatch.destinationWarehouseName,
          dispatch.mocNo,
          dispatch.challanNo,
          dispatch.orderNumber,
          dispatch.poNumber
        ].filter(Boolean).join(' ').toLowerCase();

        if (!haystack.includes(filters.customerName.toLowerCase())) return false;
      }

      return true;
    });

    if (sortColumn) {
      filterableDispatches.sort((a, b) => {
        const aValue = getSortValue(a, sortColumn);
        const bValue = getSortValue(b, sortColumn);

        if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
        return aValue < bValue ? 1 : -1;
      });
    }

    return filterableDispatches;
  }, [dispatches, sortColumn, sortDirection, filters]);

  const paginatedDispatches = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedDispatches.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedDispatches, currentPage, itemsPerPage]);

  const formatDate = (value) => {
    if (!value) return '---';
    const date = value?.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return '---';
    return date.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getDisplayPartyName = (dispatch) => {
    if (dispatch.typeOfMOC === 'Interbranch Transfer') {
      return dispatch.destinationWarehouseName || dispatch.customerName || '---';
    }
    return dispatch.customerName || '---';
  };

  const getDisplayPartySubtext = (dispatch) => {
    if (dispatch.typeOfMOC === 'Interbranch Transfer') {
      return dispatch.warehouse || 'Warehouse Transfer';
    }
    return dispatch.projectName || dispatch.shippingAddress || '---';
  };

  const getDisplayReference = (dispatch) => {
    if (dispatch.typeOfMOC === 'Interbranch Transfer') {
      return dispatch.poNumber || dispatch.challanNo || dispatch.mocNo || '---';
    }
    return dispatch.orderNumber || dispatch.challanNo || dispatch.mocNo || '---';
  };

  const handleViewDetail = (dispatchId) => {
    navigate(`/ops/inbound-que/${dispatchId}`, { state: { source: 'inwards-queue' } });
  };

  const handleCreate = (event, dispatch) => {
    event.stopPropagation();

    if (dispatch.typeOfMOC === 'Interbranch Transfer') {
      navigate('/ops/pickup-challan/new', {
        state: { source: 'inwards-queue', dispatchId: dispatch.id }
      });
      return;
    }

    navigate('/ops/mic/new', {
      state: { source: 'inwards-queue', dispatchId: dispatch.id }
    });
  };

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: isMobile ? '10px' : '0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
        <ArrowDownLeft size={28} color="#2563eb" />
        <h2 style={{ margin: 0 }}>Inwards Que ({filteredAndSortedDispatches.length})</h2>
      </div>

      <div style={filterSectionStyle}>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>From Date</label>
          <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>To Date</label>
          <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={filterInputStyle} />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>Customer / Destination</label>
          <input type="text" name="customerName" value={filters.customerName} onChange={handleFilterChange} placeholder="Search Customer / MOC / DC" style={filterInputStyle} />
        </div>
        <div style={filterInputGroupStyle}>
          <label style={filterLabelStyle}>MOC Type</label>
          <select name="mocType" value={filters.mocType} onChange={handleFilterChange} style={filterInputStyle}>
            <option value="">All</option>
            <option value="Sales">Sales</option>
            <option value="Rental">Rental</option>
            <option value="Interbranch Transfer">Interbranch Transfer</option>
          </select>
        </div>
      </div>

      {dispatches.length === 0 ? (
        <div style={{ padding: '50px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px', border: '1px dashed #cbd5e1' }}>
          <Clock size={48} color="#cbd5e1" style={{ marginBottom: '15px' }} />
          <h3 style={{ color: '#64748b' }}>Inwards queue is empty</h3>
          <p style={{ color: '#94a3b8' }}>Delivered dispatches will appear here once they are ready for inward processing.</p>
        </div>
      ) : (
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ backgroundColor: '#f9fafb' }}>
              <tr>
                <th style={thStyle}>Delivered On</th>
                <th style={{ ...thStyle, textAlign: 'left' }}>Customer / Destination</th>
                <th style={thStyle}>Ref Number</th>
                <th style={thStyle}>DC No.</th>
                <th style={thStyle}>MOC Type</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedDispatches.map((dispatch) => (
                <tr
                  key={dispatch.id}
                  style={{ borderBottom: '1px solid #e5e7eb', cursor: 'pointer' }}
                  onClick={() => handleViewDetail(dispatch.id)}
                >
                  <td style={tdStyle}>{formatDate(dispatch.deliveredAt || dispatch.updatedAt)}</td>
                  <td style={{ ...tdStyle, textAlign: 'left' }}>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{getDisplayPartyName(dispatch)}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>{getDisplayPartySubtext(dispatch)}</div>
                  </td>
                  <td style={tdStyle}>
                    <strong style={{ fontSize: '13px' }}>{getDisplayReference(dispatch)}</strong>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontWeight: '600', color: '#2563eb' }}>{dispatch.challanNo || '---'}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#eff6ff', color: '#2563eb' }}>
                      {dispatch.typeOfMOC || '---'}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={(event) => handleCreate(event, dispatch)}
                      style={actionBtnStyle}
                    >
                      <Plus size={14} /> {dispatch.typeOfMOC === 'Interbranch Transfer' ? 'Create PC' : 'Create MIC'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// --- STYLES ---
const thStyle = { padding: '15px', textAlign: 'center', fontSize: '11px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #f3f4f6' };
const tdStyle = { padding: '15px', fontSize: '13px', textAlign: 'center', color: '#374151' };
const filterSectionStyle = { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap', padding: '15px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' };
const filterInputGroupStyle = { display: 'flex', flexDirection: 'column', minWidth: '150px', flex: '1' };
const filterLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#6b7280', marginBottom: '4px', textTransform: 'uppercase' };
const filterInputStyle = { width: '100%', padding: '8px', borderRadius: '5px', border: '1px solid #d1d5db', fontSize: '13px', outline: 'none' };
const actionBtnStyle = { backgroundColor: '#059669', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', margin: '0 auto' };

export default InwardsQue;
